import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  Tooltip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Chip,
  Stack,
  InputAdornment,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import DataGrid from './ui/DataTable';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, useTheme } from '@mui/material/styles';

import axiosInstance from './axiosInstance';
import { isValidEmail } from "../utils/validation";
import { useSnackbar } from "../providers/SnackbarProvider";
import SectionCard from './ui/SectionCard';
import EmptyState from './ui/EmptyState';
import { titleOptions, typeOptions, priorityOptions } from '../constants/leadOptions';
import { useBreadcrumbs } from './BreadcrumbBar';

const TYPE_LABELS = Object.fromEntries(typeOptions.map((o) => [o.value, o.label]));

// Dieselben Farbwelten wie auf der Kontakt-Detailseite
// Design 3.0: tiefere Töne (Pastell-Pill-Paare entstehen per alpha());
// Dark: aufgehellte Pendants der Dark-Semantikfarben — die tiefen Töne wären unleserlich
const PRIORITY_COLOR = (theme) => (theme.palette.mode === 'dark'
  ? { 'A+': '#F07A72', A: '#F0955E', B: '#E2A33B', C: '#98A2B5', D: '#7A8598' }
  : { 'A+': '#B91C1C', A: '#C2570C', B: '#B45309', C: '#647084', D: '#94A3B8' });
const TYPE_COLOR = (theme) => (theme.palette.mode === 'dark'
  ? { Customer: '#818CF8', Partner: '#4CC38A', Supplier: '#E2A33B', Other: '#4EC8C0' }
  : { Customer: '#4F46E5', Partner: '#15803D', Supplier: '#B45309', Other: '#0E9E97' });

const initials = (fn, ln) => `${fn?.[0] ?? ''}${ln?.[0] ?? ''}`.toUpperCase() || '?';

// Leerzustand bei Filter/Suche ohne Treffer (D4) — Slots erwarten Komponenten
const NoResults = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);

const EMPTY_CONTACT = {
  salutation: '', title: '', first_name: '', last_name: '', type: 'Customer',
  priority: 'C', position: '', email: '', phone: '', mobile: '', website: '',
  street: '', city: '', zip_code: '', notes: '', account: '',
};

function ContactList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState({});
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);

  useBreadcrumbs([{ label: 'Kontakte' }]);

  useEffect(() => {
    const load = async () => {
      try {
        const [contactsRes, accountsRes] = await Promise.all([
          axiosInstance.get('/contacts/'),
          axiosInstance.get('/accounts/'),
        ]);
        setContacts(contactsRes.data);
        setAccounts(Object.fromEntries(accountsRes.data.map((a) => [a.id, a])));
      } catch (error) {
        console.error('Error loading contacts', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const accountName = (accountId) => (accountId && accounts[accountId] ? accounts[accountId].name : '');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (typeFilter !== 'ALL' && c.type !== typeFilter) return false;
      if (!q) return true;
      return [c.first_name, c.last_name, c.email, c.position, accountName(c.account)]
        .some((f) => (f || '').toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, search, typeFilter, accounts]);

  // ---------- Leerzustände (D4) ----------
  // Suche/Typ-Filter laufen außerhalb der Grid — bei aktivem Filter ohne
  // Treffer sieht die Grid daher ebenfalls „keine Zeilen". NoRows unterscheidet
  // deshalb selbst zwischen echter Leere und leergefiltertem Bestand.
  const NoRows = useCallback(() => (
    contacts.length ? <NoResults /> : (
      <EmptyState
        title="Noch keine Kontakte."
        hint="Der erste Eintrag macht das Adressbuch lebendig."
        actionLabel="Neuen Kontakt anlegen"
        onAction={() => setCreateDialogOpen(true)}
      />
    )
  ), [contacts.length]);

  // ---------- Zellen ----------
  const renderContactCell = (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
      <Avatar sx={{ width: 32, height: 32, fontSize: '0.78rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark', flex: 'none' }}>
        {initials(params.row.first_name, params.row.last_name)}
      </Avatar>
      <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {params.row.first_name} {params.row.last_name}
          </Typography>
          {params.row.is_converted_lead && (
            <Tooltip title="Aus Lead konvertiert">
              <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {params.row.email || '—'}
        </Typography>
      </Box>
    </Box>
  );

  const renderTypeCell = (params) => {
    if (!params.value) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const color = TYPE_COLOR(theme)[params.value] || theme.palette.text.secondary;
    return (
      <Chip size="small" label={TYPE_LABELS[params.value] || params.value}
        sx={{ bgcolor: alpha(color, 0.13), color, fontWeight: 600 }} />
    );
  };

  const renderPriorityCell = (params) => {
    if (!params.value) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const color = PRIORITY_COLOR(theme)[params.value] || theme.palette.text.secondary;
    return (
      <Chip size="small" label={params.value}
        sx={{ bgcolor: alpha(color, 0.13), color, fontWeight: 700, minWidth: 42 }} />
    );
  };

  const columns = [
    {
      field: 'name', headerName: 'Kontakt', minWidth: 230, flex: 1.5,
      valueGetter: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
      renderCell: renderContactCell,
    },
    {
      field: 'account', headerName: 'Unternehmen', minWidth: 170, flex: 1.1,
      valueGetter: (value) => accountName(value),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: params.value ? 'text.primary' : 'text.disabled' }} noWrap>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'position', headerName: 'Position', minWidth: 140, flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>{params.value || '—'}</Typography>
      ),
    },
    { field: 'type', headerName: 'Typ', minWidth: 110, flex: 0.6, renderCell: renderTypeCell },
    { field: 'priority', headerName: 'Priorität', minWidth: 90, flex: 0.5, renderCell: renderPriorityCell },
    {
      field: 'phone', headerName: 'Telefon', minWidth: 140, flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>{params.value || '—'}</Typography>
      ),
    },
  ];

  // ---------- Anlegen ----------
  const handleInputChange = (e) => setNewContact((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleClose = () => {
    setCreateDialogOpen(false);
    setNewContact(EMPTY_CONTACT);
  };

  const handleCreateContact = async () => {
    if (!newContact.first_name || !newContact.last_name) {
      showSnackbar('Vor- und Nachname sind erforderlich', 'error');
      return;
    }
    if (!newContact.email || !isValidEmail(newContact.email)) {
      showSnackbar('Ungültige E-Mail-Adresse', 'error');
      return;
    }
    try {
      const payload = { ...newContact, account: newContact.account || null };
      const response = await axiosInstance.post('/contacts/', payload);
      setContacts([response.data, ...contacts]);
      handleClose();
      showSnackbar('Kontakt erfolgreich erstellt', 'success');
    } catch (error) {
      console.error('Error creating contact:', error);
      showSnackbar('Fehler beim Erstellen', 'error');
    }
  };

  const typeFilters = [{ key: 'ALL', label: 'Alle' }, ...typeOptions.map((o) => ({ key: o.value, label: o.label }))];

  return (
    <Box>
      {/* Kopfzeile */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">Kontakte</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {filtered.length} {filtered.length === 1 ? 'Kontakt' : 'Kontakte'}
            {(search || typeFilter !== 'ALL') ? ` (von ${contacts.length})` : ''}
          </Typography>
        </Box>
        <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }}>
          Neuer Kontakt
        </Button>
      </Stack>

      {/* Suche + stille Typ-Filter */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', md: 'center' }}>
        <TextField
          size="small" placeholder="Kontakte durchsuchen …" value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, maxWidth: 400, bgcolor: 'background.paper' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment> }}
        />
        <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
          {typeFilters.map((f) => {
            const active = typeFilter === f.key;
            return (
              <Box
                key={f.key} role="button" tabIndex={0}
                onClick={() => setTypeFilter(f.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTypeFilter(f.key); } }}
                sx={{
                  fontSize: '0.8rem', fontWeight: 600, px: active ? 1.25 : 0.75, py: 0.3, borderRadius: 999,
                  cursor: 'pointer', userSelect: 'none',
                  color: active ? 'primary.main' : 'text.disabled',
                  bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                  '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                }}
              >
                {f.label}
              </Box>
            );
          })}
        </Box>
      </Stack>

      {/* Tabelle als Fläche */}
      <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          onRowClick={(params) => navigate(`/contacts/${params.row.id}`)}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={56}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ noRowsOverlay: NoRows, noResultsOverlay: NoResults }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
          }}
        />
      </SectionCard>

      {/* Kontakt-anlegen-Dialog */}
      <Dialog open={createDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Neuen Kontakt erstellen</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" color="text.secondary">Person</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select name="salutation" label="Anrede" value={newContact.salutation} onChange={handleInputChange} fullWidth>
                  <MenuItem value="">Keine Auswahl</MenuItem>
                  <MenuItem value="Herr">Herr</MenuItem>
                  <MenuItem value="Frau">Frau</MenuItem>
                  <MenuItem value="Divers">Divers</MenuItem>
                </TextField>
                <TextField select name="title" label="Titel" value={newContact.title ?? ''} onChange={handleInputChange} fullWidth>
                  {titleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField name="first_name" label="Vorname" value={newContact.first_name} onChange={handleInputChange} fullWidth required />
                <TextField name="last_name" label="Nachname" value={newContact.last_name} onChange={handleInputChange} fullWidth required />
                <TextField select name="account" label="Unternehmen" value={newContact.account} onChange={handleInputChange} fullWidth>
                  <MenuItem value=""><em>Keine Auswahl</em></MenuItem>
                  {Object.values(accounts).map((account) => (
                    <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>
                  ))}
                </TextField>
                <TextField name="position" label="Position" value={newContact.position} onChange={handleInputChange} fullWidth />
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Erreichbarkeit</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField name="email" label="E-Mail" type="email" value={newContact.email} onChange={handleInputChange} fullWidth required />
                <TextField name="phone" label="Telefon (Festnetz)" value={newContact.phone} onChange={handleInputChange} fullWidth />
                <TextField name="mobile" label="Telefon (Mobil)" value={newContact.mobile} onChange={handleInputChange} fullWidth />
                <TextField name="website" label="Webseite" value={newContact.website} onChange={handleInputChange} fullWidth />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 3 } }}>
              <Typography variant="overline" color="text.secondary">Klassifizierung</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select name="type" label="Typ" value={newContact.type} onChange={handleInputChange} fullWidth>
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select name="priority" label="Priorität" value={newContact.priority} onChange={handleInputChange} fullWidth>
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Adresse</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField name="street" label="Straße" value={newContact.street} onChange={handleInputChange} fullWidth />
                <TextField name="zip_code" label="PLZ" value={newContact.zip_code} onChange={handleInputChange} fullWidth />
                <TextField name="city" label="Stadt" value={newContact.city} onChange={handleInputChange} fullWidth />
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Notizen</Typography>
              <Box sx={{ mt: 1 }}>
                <TextField name="notes" label="Notizen" value={newContact.notes} onChange={handleInputChange} multiline rows={4} fullWidth />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button onClick={handleCreateContact} variant="contained" disabled={!newContact.first_name || !newContact.last_name || !newContact.email}>
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContactList;
